// R5.7 — `EntryLabel`: THE ONE heading every per-entry group on the review surface carries.
//
// Live smoke (2026-08-22) found an entry without a stated `role_title` rendering as an anonymous facet strip, an
// anonymous flag group, an anonymous confidence line and an anonymous recommendation card — three or four pointers
// at "this entry" with nothing saying WHICH. Every one of those call sites had its own `entryLabels.get(id)` and its
// own "render nothing when absent" branch. This component replaces all of them with one answer, built from one
// rule (`entryHeadingOf` in `evidenceIndex.ts`): the applicant's own `role_title`, else `institution`, else
// `raw_text`, plus the entry's dates.
//
// THE EXACTNESS CONTRACT IS KEPT, AND IT IS KEPT IN CSS. `raw_text` can be a paragraph. The heading is still the
// WHOLE string, rendered through `ApplicantText` as a single text node — no `slice`, no ellipsis character, no
// "…more" this surface authored — and `.rubric-entry-label-text` clamps it to two lines VISUALLY (`-webkit-line-
// clamp`). The DOM and the accessibility tree carry the full text: a screen reader hears the whole heading, a
// find-in-page finds it, the exactness sweep (T-VERB-1) sees the byte-identical value. Truncating in JavaScript
// would render a string the applicant did not write (their words, cut) under `applicant_verbatim` provenance.
//
// THE DATES ARE DATA, NOT WORDS. `date_start`/`date_end` are `YYYY`/`YYYY-MM` normalizations (§2.2), so they go
// through the registered typed-value templates — `CS-ENTRY-DATE-RANGE` when both are stated, `CS-VALUE-DATE` when
// one is — exactly as Layer 1's fact rows already render a date-partial, and are scanned as system copy. A
// normalized partial is not the applicant's own words and never renders as if it were.
//
// NOTHING HERE IS AN ATTRIBUTE. The label text is a text node; the only attributes this file writes are a system
// `data-testid`, the closed `data-entry-label-source` enum, and a class name (§1.5 rule 2).
import type { CSSProperties } from 'react';
import type { EntryHeading } from './evidenceIndex';
import { ApplicantText, Copy } from './renderContent';
import { MUTED_STYLE } from './styles';

const DATE_STYLE: Readonly<CSSProperties> = Object.freeze({
  ...MUTED_STYLE,
  display: 'block',
  margin: 'var(--space-1) 0 0',
  fontWeight: 'var(--fw-regular)',
});

/**
 * The label. Renders NOTHING when there is no heading (a malformed ledger row with nothing stated at any rung) —
 * never a placeholder, never an internal id, never an invented "Entry 1" (which would be both a fabricated label
 * and a count).
 *
 * `as` picks the wrapping element: an `h3` where the label heads a card in the heading outline, a `p` where it
 * is a muted pointer inside a list item, a `span` where it sits inside another control (a link, a button's
 * visually-hidden context). The CONTENT is identical in every case.
 */
export function EntryLabel({
  heading,
  as = 'p',
  style,
}: {
  heading: EntryHeading | null | undefined;
  as?: 'h3' | 'p' | 'span';
  style?: CSSProperties;
}) {
  if (!heading) return null;
  const Tag = as;
  return (
    <Tag style={style} data-testid="rubric-entry-label" data-entry-label-source={heading.source}>
      <span className="rubric-entry-label-text">
        <ApplicantText text={heading.text} />
      </span>
      <EntryDates heading={heading} inline={as === 'span'} />
    </Tag>
  );
}

/** The date range, through the registered templates. Nothing when neither date is stated. */
function EntryDates({ heading, inline }: { heading: EntryHeading; inline: boolean }) {
  const { date_start, date_end } = heading;
  if (date_start === null && date_end === null) return null;
  const style = inline ? { ...MUTED_STYLE, margin: 0 } : DATE_STYLE;
  return (
    <span style={style} data-testid="rubric-entry-dates">
      {inline && ' '}
      {date_start !== null && date_end !== null ? (
        <Copy id="CS-ENTRY-DATE-RANGE" slots={{ start: date_start, end: date_end }} />
      ) : (
        <Copy id="CS-VALUE-DATE" slots={{ date: (date_start ?? date_end) as string }} />
      )}
    </span>
  );
}
